/* eslint-disable import/order */
import React, { FunctionComponent, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import Banner from "../../components/Banner";
import { Breadcrumbs } from "../../components/Breadcrumbs";
import Hero from "../../components/Hero";
import { HeroVersion } from "../../types/IHero";
import Hero4 from "../../components/Hero4/Hero4";
import Cookie from "js-cookie";
import Hero5 from "../../components/Hero5";

import JsonLd from "../../components/JsonLd";
import { PartnerProvider } from "../../components/Partner";
import { GlobalPartnerStyles } from "../../components/Partner/GlobalPartnerStyles";
import { Meta } from "../../components/Meta";
import { Layout } from "../Layout";
import { PageTitleProvider } from "../../components/PageTitle";
import {
  IComponent,
  IModularTemplateProps,
} from "./types";
import { getAccordionPanel, GetAccordionPanelProps } from "./modules/accordionPanel";
import { getAsSeenOnPanel } from "./modules/asSeenOnPanel";
import { getBigLinkPanel, GetBigLinkPanelProps } from "./modules/bigLinkPanel";
import { getComparisonPanel, GetComparisonPanelProps } from "./modules/comparisonPanel";
import {
  getContentWithSidebarPanel,
  GetContentWithSidebarPanelProps,
} from "./modules/contentWithSidebarPanel";
import { getFeaturesWithCTAs, GetFeaturesWithCTAsProps } from "./modules/featuresWithCTAs";
import { getMortgageComparisonPanel } from "./modules/mortgageComparisonPanel";
import { IAsSeenOnPanelProps } from "../../components/AsSeenOnPanel";
import { getGridPanel, GetGridPanelProps } from "./modules/gridPanel";
import { getPullOutBlocksPanel, GetPullOutBlocksPanelProps } from "./modules/pullOutBlocksPanel";
import { getSplashPanel, GetSplashPanelProps } from "./modules/splashPanel";
import { getAuthorByLine, GetAuthorByLineProps } from "./modules/authorByLine";
import { getCalculatorPanel } from "./modules/calculatorPanel";
import { getTrustPilotPanel, GetTrustPilotPanelProps } from "./modules/trustPilotPanel";
import { getArticlePanel, GetArticlePanelProps } from "./modules/articlePanel";
import "./styles.scss";
import { getLinksPanel, GetLinksPanelProps } from "./modules/linksPanel";
import { getHorizontalCardsPanel, GetHorizontalCardsPanelProps } from "./modules/horizontalCardsPanel";
import { getVerticalCardsPanel, GetVerticalCardsPanelProps } from "./modules/verticalCardsPanel";
import { getLenderPanel, GetLenderPanelProps } from "./modules/lenderPanel";
import SnappingContent from "../SnappingContent";
import FloatingButton from "../../components/FloatingButton";
import Hero6 from "../../components/Hero6";
import HeroSmartBuyer from "../../components/HeroSmartBuyer";
import HeroSimpleBetterV2 from "../../components/HeroSimpleV2";
import HeroMIP from "../../components/HeroMIP";
import { HeroGreenWithGlossyText } from "../../components/HeroGreenWithGlossyText";
import classNames from "classnames";
import HeroChecklist from "../../components/HeroChecklist";
import { HeroMobileDownload } from "../../components/HeroMobileDownload";
import { HeroFF2 } from "../../components/HeroFF2";
import {
  getSpecialisedStructuredData,
  GetSpecialisedStructuredDataProps
} from "./modules/specialisedStructuredData";

export const componentByTypeName = (
  typename: string,
  props: IComponent,
  idx: number,
  updatedAt: string,
  betterV2?: boolean,
  snapScroll?: boolean,
) => {
  // TODO: fix any type
  const modules: any = {
    ContentfulAccordionPanel: () => getAccordionPanel({ ...props, snapScroll } as GetAccordionPanelProps),
    ContentfulAuthorByline: () => getAuthorByLine({ ...props, updatedAt } as GetAuthorByLineProps),
    ContentfulAsSeenOnPanel: () => getAsSeenOnPanel(props as IAsSeenOnPanelProps),
    ContentfulBigLinkPanel: () => getBigLinkPanel(props as GetBigLinkPanelProps),
    ContentfulCalculatorPanel: () => getCalculatorPanel({ ...props, idx }),
    ContentfulComparisonPanel: () => getComparisonPanel(props as GetComparisonPanelProps),
    ContentfulContentWithSidebarPanel: () => getContentWithSidebarPanel(
      props as GetContentWithSidebarPanelProps,
    ),
    ContentfulFeaturesWithCtas: () => getFeaturesWithCTAs(props as GetFeaturesWithCTAsProps),
    ContentfulGridPanel: () => getGridPanel({ ...props, betterV2 } as GetGridPanelProps),
    ContentfulMortgageComparisonPanel: () => getMortgageComparisonPanel(props),
    ContentfulPullOutBlocksPanel: () => getPullOutBlocksPanel(props as GetPullOutBlocksPanelProps),
    ContentfulSplashPanel: () => getSplashPanel({ ...props, betterV2, snapScroll }as GetSplashPanelProps),
    ContentfulTrustpilotPanel: () => getTrustPilotPanel(props as GetTrustPilotPanelProps),
    ContentfulArticlePanel: () => getArticlePanel(props as GetArticlePanelProps),
    ContentfulLinksPanel: () => getLinksPanel(props as GetLinksPanelProps),
    ContentfulHorizontalCardsPanel: () => getHorizontalCardsPanel(props as GetHorizontalCardsPanelProps),
    ContentfulVerticalCardsPanel: () => getVerticalCardsPanel(props as GetVerticalCardsPanelProps),
    ContentfulLenderPanel: () => getLenderPanel(props as GetLenderPanelProps),
    ContentfulSpecialisedStructuredData: () => getSpecialisedStructuredData(props as GetSpecialisedStructuredDataProps),
  };
  const Component = modules[typename];
  return Component ? Component(props) : null;
};

export const HEADER_ANCHOR = "header-anchor";

const ConditionalSnappingContent = (
  {
    children, snapScroll,
  }: { children: React.ReactNode; snapScroll: boolean; },
) => (snapScroll
  ? (
    <SnappingContent>
      {children}
    </SnappingContent>
  )
  : <>{ children }</>);

export const Main: FunctionComponent<IModularTemplateProps> = ({ data }) => {
  const {
    hero,
    modules,
    structuredData,
    updatedAt,
    shortTitle,
    urlPath,
    parentPage,
    betterV2 = false,
    snapScroll,
  } = data.contentfulModularTemplate;

  const snapScrollWithDefault = (snapScroll === null || snapScroll === undefined) ? betterV2 : snapScroll;

  return (
    <div className={classNames("modular-content_container", {
      "modular-content_container--betterv2": betterV2,
      "modular-content_container--betterv2-no-snap": betterV2 && !snapScrollWithDefault,
    })}
    >
      {structuredData
        && structuredData.internal
        && structuredData.internal.content && (
          <JsonLd data={JSON.parse(structuredData.internal.content)} />
      )}
      {betterV2 && parentPage && urlPath && (
        <div className={snapScroll ? "modular-content_container__breadcrumbs-with-snap-scroll" : undefined}>
          <Breadcrumbs
            parentPage={parentPage}
            shortTitle={shortTitle}
            urlPath={urlPath}
            betterV2={betterV2}
          />
        </div>
      )}
      <ConditionalSnappingContent snapScroll={snapScrollWithDefault}>
        {hero && hero.heroVersion === HeroVersion.V1 && <Hero {...hero} />}
        {hero && hero.heroVersion === HeroVersion.V4 && <Hero4 {...hero} />}
        {hero && hero.heroVersion === HeroVersion.V5 && <Hero5 {...hero} />}
        {hero && hero.heroVersion === HeroVersion.V6 && <Hero6 {...hero} />}
        {hero && hero.heroVersion === HeroVersion.HeroSmartBuyer && <HeroSmartBuyer {...hero} />}
        {hero && hero.heroVersion === HeroVersion.HeroSimpleBetterV2 && <HeroSimpleBetterV2 {...hero} />}
        {hero && hero.heroVersion === HeroVersion.HeroMIP && <HeroMIP {...hero} />}
        {hero && hero.heroVersion === HeroVersion.HeroGreenWithGlossyText && <HeroGreenWithGlossyText {...hero} />}
        {hero && hero.heroVersion === HeroVersion.HeroChecklist && <HeroChecklist {...hero} />}
        {hero && hero.heroVersion === HeroVersion.HeroMobileDownload && <HeroMobileDownload {...hero} />}
        {hero && hero.heroVersion === HeroVersion.FF2 && <HeroFF2 {...hero} />}

        {!betterV2 && parentPage && urlPath && (
        <Breadcrumbs
          parentPage={parentPage}
          shortTitle={shortTitle}
          urlPath={urlPath}
          betterV2={betterV2}
        />
        )}
        {Array.isArray(modules)
        && modules.map((m, idx) => {
          const { __typename, ...props } = m;

          return componentByTypeName(
            __typename,
            transformModulesProps(props, idx),
            idx,
            updatedAt,
            betterV2,
            snapScroll,
          );
        })}
      </ConditionalSnappingContent>
    </div>
  );
};

export const transformModulesProps = (moduleProps: any, index: number) => (
  {
    ...moduleProps,
    headerAnchor: index === 0 ? HEADER_ANCHOR : undefined,
  }
);

export const ModularTemplate = ({ data }: IModularTemplateProps) => {
  // TODO: rename section to sections
  const {
    canonicalUrlPath,
    urlPath,
    usHrefLang,
    metaTitle,
    metaDescription,
    pageTitle,
    noIndex,
    hideHamburger,
    partner,
    enableAbTesting,
    includeAggregateRating,
    hideHeader,
    hideFooter,
    hideLogin,
    showSmallFooter,
    betterV2,
    snapScroll,
    applyLinks,
  } = data.contentfulModularTemplate;

  const {
    bannerText, bannerLinkText, bannerLinkUrl, topBannerDisplay,
  } = data.allContentfulGlobalSettings.edges[0].node;
  const shouldDisplayBanner = topBannerDisplay === "show on all pages"
    || (topBannerDisplay === "show on homepage" && urlPath === "/");

  const [isCookieDismissed, setIsCookieDismissed] = useState<boolean>(false);

  const onCookieStatusChange = () => {
    setIsCookieDismissed(true);
  };

  useEffect(() => {
    setIsCookieDismissed(!!Cookie.get("cookieconsent_status"));
  }, []);

  return (
    <PartnerProvider partnerStr={partner}>
      <PageTitleProvider pageTitle={pageTitle}>
        <GlobalPartnerStyles>
          {usHrefLang && (
            <ModularTemplateHead
              urlPath={urlPath}
              usHrefLang={usHrefLang}
            />
          )}
          <Layout
            hideHeader={hideHeader}
            hideHamburger={hideHamburger}
            enableAbTesting={enableAbTesting}
            hideFooter={hideFooter}
            hideLogin={hideLogin}
            showSmallFooter={showSmallFooter}
            onCookieStatusChange={onCookieStatusChange}
            betterV2={betterV2}
            snapScroll={(snapScroll === null || snapScroll === undefined) ? betterV2 : snapScroll}
            isCookieDismissed={isCookieDismissed}
            floatingButton={applyLinks ? <FloatingButton buttonText="Apply" applyLinks={applyLinks} /> : undefined}
          >
            <Meta
              title={pageTitle}
              metaTitle={metaTitle}
              description={metaDescription && metaDescription.metaDescription}
              pathname={urlPath}
              canonicalPathname={canonicalUrlPath}
              noIndex={!!noIndex}
              includeAggregateRating={includeAggregateRating}
            />
            <Banner
              initialText={bannerText}
              linkText={bannerLinkText}
              linkUrl={bannerLinkUrl}
              show={shouldDisplayBanner}
            />
            <Main data={data} />
          </Layout>
        </GlobalPartnerStyles>
      </PageTitleProvider>
    </PartnerProvider>
  );
};

type ModularTemplateHeadProps = Pick<
  IModularTemplateProps["data"]["contentfulModularTemplate"],
  "urlPath" | "usHrefLang"
  >;
export const ModularTemplateHead = (
  { urlPath, usHrefLang }: ModularTemplateHeadProps,
) => {
  const revisedUrlPath = urlPath?.endsWith("/")
    ? urlPath.replace(/.$/, "")
    : urlPath;
  return (
    <Helmet>
      <link rel="alternate" hrefLang="en-gb" href={`https://${DOMAIN}${revisedUrlPath}`} />
      <link rel="alternate" hrefLang="en-us" href={`${usHrefLang}`} />
    </Helmet>
  );
};
